import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    InputAdornment,
    Select,
    MenuItem,
    Avatar
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, Mail, Phone, MapPin, Search } from 'lucide-react';
import { getOrganizers, createOrganizer, updateOrganizer, deleteOrganizer } from '../../Services/ContentHub/SearchOrganizedApi';
import { getAllFlags } from '../../Services/ContentHub/FlagsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { generateSlug, normalizeSlugInput, cleanSlugFinal } from '../../utils.js/slugUtils';
import GlobalPagination from '../../Components/Common/Globalpagination';

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const GetAllOrganizers = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [organizers, setOrganizers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        page: 1,
        pageSize: 10
    });
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 10
    });

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [countryCodes, setCountryCodes] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        countryCode: '+91',
        email: '',
        phone: '',
        location: '',
        website: '',
        tripAdvisorUrl: '',
        mapIframe: '',
        description: '',
        isActive: true,
        logo: null,
        heading: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        metaOutlet: [],
        canonicalUrl: '',
        isIndex: true
    });
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [modalError, setModalError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setFormData(prev => ({
            ...prev,
            metaOutlet: [...(prev.metaOutlet || []), trimmed]
        }));
        setNewMetaOutletInput('');
        if (fieldErrors.metaOutlet) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.metaOutlet;
                return updated;
            });
        }
    };

    const handleRemoveMetaOutletItem = (index) => {
        setFormData(prev => ({
            ...prev,
            metaOutlet: (prev.metaOutlet || []).filter((_, idx) => idx !== index)
        }));
    };

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Debounce function
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    const fetchOrganizers = useCallback(async () => {
        try {
            setLoading(true);
            const params = { page: filters.page };
            if (filters.pageSize !== 10) params.limit = filters.pageSize;
            const response = await getOrganizers(
                filters.search,
                filters.status,
                params
            );
            if (response && response.success) {
                setOrganizers(response.data);
                setPagination({
                    totalRecords: response.totalRecords || 0,
                    totalPages: response.totalPages || 1,
                    currentPage: response.currentPage || 1,
                    pageSize: response.pageSize || 10
                });
            } else {
                setError("Failed to load organizers.");
            }
        } catch (err) {
            setError("Failed to fetch organizers. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Debounced fetch function
    const debouncedFetch = useMemo(() => debounce(fetchOrganizers, 500), [fetchOrganizers]);

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1 // Reset page when other filters change
        }));
    };

    useEffect(() => {
        debouncedFetch();
    }, [debouncedFetch]);

    // Fetch country codes
    useEffect(() => {
        const fetchCountryCodes = async () => {
            try {
                const response = await getAllFlags();
                if (response && response.success && Array.isArray(response.data)) {
                    const activeFlags = response.data.filter(item => item.isActive !== false);
                    setCountryCodes(activeFlags.length > 0 ? activeFlags : response.data);
                }
            } catch (err) {
                console.error("Error fetching country codes:", err);
            }
        };
        fetchCountryCodes();
    }, []);

    const defaultCountryCode = React.useMemo(() => {
        const defaultFlag = countryCodes.find(item => item.isDefault);
        return defaultFlag?.countryCode || '+91';
    }, [countryCodes]);

    const displayCountryCodes = React.useMemo(() => {
        let list = [...countryCodes];
        const currentCode = formData.countryCode || defaultCountryCode;
        if (!list.some(item => item.countryCode === currentCode)) {
            list.unshift({ countryCode: currentCode, name: '' });
        }
        if (list.length === 0) {
            list = [
                { countryCode: '+91', name: 'India' },
                { countryCode: '+1', name: 'USA/Canada' },
                { countryCode: '+44', name: 'UK' },
            ];
        }
        return list;
    }, [countryCodes, formData.countryCode, defaultCountryCode]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                name: item.name || '',
                slug: item.slug ? cleanSlugFinal(item.slug) : (item.name ? generateSlug(item.name) : ''),
                countryCode: item.countryCode || defaultCountryCode,
                email: item.email || '',
                phone: item.phone || '',
                location: item.location || '',
                website: item.website || '',
                tripAdvisorUrl: item.tripAdvisorUrl || '',
                mapIframe: item.mapIframe || '',
                description: item.description || '',
                isActive: item.isActive !== undefined ? item.isActive : true,
                logo: null,
                heading: item.heading || '',
                metaTitle: item.metaTitle || '',
                metaDescription: item.metaDescription || '',
                metaKeywords: item.metaKeywords || '',
                metaOutlet: safeParseArray(item.metaOutlet),
                canonicalUrl: item.canonicalUrl || '',
                isIndex: item.isIndex !== undefined ? (item.isIndex === "true" || item.isIndex === true) : true
            });
            setNewMetaOutletInput('');
            setIsSlugManuallyEdited(false);
            setLogoPreview(item.logo || null);
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                name: '',
                slug: '',
                countryCode: defaultCountryCode,
                email: '',
                phone: '',
                location: '',
                website: '',
                tripAdvisorUrl: '',
                mapIframe: '',
                description: '',
                isActive: true,
                logo: null,
                heading: '',
                metaTitle: '',
                metaDescription: '',
                metaKeywords: '',
                metaOutlet: [],
                canonicalUrl: '',
                isIndex: true
            });
            setNewMetaOutletInput('');
            setIsSlugManuallyEdited(false);
            setLogoPreview(null);
        }
        setError(null);
        setModalError(null);
        setFieldErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({
            name: '',
            slug: '',
            countryCode: defaultCountryCode,
            email: '',
            phone: '',
            location: '',
            website: '',
            tripAdvisorUrl: '',
            mapIframe: '',
            description: '',
            isActive: true,
            logo: null,
            heading: '',
            metaTitle: '',
            metaDescription: '',
            metaKeywords: '',
            metaOutlet: [],
            canonicalUrl: '',
            isIndex: true
        });
        setNewMetaOutletInput('');
        setIsSlugManuallyEdited(false);
        setLogoPreview(null);
        setError(null);
        setModalError(null);
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === 'checkbox' ? checked : value;
        if (name === 'name') {
            newValue = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData(prev => ({
                ...prev,
                name: newValue,
                ...(!isSlugManuallyEdited ? { slug: generateSlug(newValue) } : {})
            }));
            if (fieldErrors.name) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.name;
                    return updated;
                });
            }
            if (!isSlugManuallyEdited && fieldErrors.slug) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.slug;
                    return updated;
                });
            }
            return;
        }
        if (name === 'phone') {
            newValue = value.replace(/\D/g, '').slice(0, 10);
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        if (name === 'location') {
            if (value && value.trim() && !/^[a-zA-Z0-9]/.test(value.trim())) {
                setFieldErrors(prev => ({
                    ...prev,
                    location: 'Location cannot start with special characters'
                }));
            } else if (fieldErrors.location) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.location;
                    return updated;
                });
            }
        }

        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const handleSlugChange = (e) => {
        const rawVal = e.target.value;
        const normalizedVal = normalizeSlugInput(rawVal);
        setFormData(prev => ({
            ...prev,
            slug: normalizedVal
        }));
        setIsSlugManuallyEdited(true);
        if (fieldErrors.slug) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.slug;
                return updated;
            });
        }
    };

    const handleSlugBlur = () => {
        if (!formData.slug) {
            setFormData(prev => ({ ...prev, slug: '' }));
            return;
        }
        const cleaned = cleanSlugFinal(formData.slug);
        if (cleaned !== formData.slug) {
            setFormData(prev => ({ ...prev, slug: cleaned }));
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    logo: 'Only image files are allowed'
                }));
                e.target.value = '';
                return;
            }

            setFormData(prev => ({
                ...prev,
                logo: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);

            if (fieldErrors.logo) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.logo;
                    return updated;
                });
            }
        }
    };

    const handleSubmit = async () => {
        setFieldErrors({});
        setModalError(null);

        const errors = {};
        if (!formData.name) {
            errors.name = "Name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            errors.name = "Name cannot contain numbers or special characters";
        }

        const finalSlug = cleanSlugFinal(formData.slug);
        if (!finalSlug) {
            errors.slug = "Slug is required";
        } else if (organizers.some(o => o.id !== currentItem?.id && cleanSlugFinal(o.slug) === finalSlug)) {
            errors.slug = "This slug is already in use by another organizer";
        }

        if (!formData.email) {
            errors.email = "Email is required";
        }

        if (!formData.mapIframe || !formData.mapIframe.trim()) {
            errors.mapIframe = "Map Iframe is required";
        }

        if (formData.location && formData.location.trim() && !/^[a-zA-Z0-9]/.test(formData.location.trim())) {
            errors.location = "Location cannot start with special characters";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setModalError("Please correct the validation errors");
            return;
        }

        setSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('slug', finalSlug);
            formDataToSend.append('countryCode', formData.countryCode || defaultCountryCode);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('location', formData.location);
            formDataToSend.append('website', formData.website);
            formDataToSend.append('tripAdvisorUrl', formData.tripAdvisorUrl);
            formDataToSend.append('mapIframe', formData.mapIframe);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('isActive', formData.isActive);

            if (formData.logo) {
                formDataToSend.append('organizerImages', formData.logo);
            }

            formDataToSend.append('heading', formData.heading ? formData.heading.trim() : '');
            formDataToSend.append('metaTitle', formData.metaTitle ? formData.metaTitle.trim() : '');
            formDataToSend.append('metaDescription', formData.metaDescription ? formData.metaDescription.trim() : '');
            formDataToSend.append('metaKeywords', formData.metaKeywords ? formData.metaKeywords.trim() : '');
            formDataToSend.append('metaOutlet', JSON.stringify(formData.metaOutlet || []));
            formDataToSend.append('canonicalUrl', formData.canonicalUrl ? formData.canonicalUrl.trim() : '');
            formDataToSend.append('isIndex', formData.isIndex ? "true" : "false");

            if (isEdit) {
                await updateOrganizer(currentItem.id, formDataToSend);
                showSnackbar("Organizer updated successfully!", "success");
            } else {
                await createOrganizer(formDataToSend);
                showSnackbar("Organizer created successfully!", "success");
            }

            handleCloseModal();
            fetchOrganizers();
        } catch (err) {
            const responseData = err.response?.data;
            const errMsg = responseData?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} organizer`;

            const mapMessageToField = (messageStr, targetObj) => {
                const msg = messageStr.toLowerCase();
                if (msg.includes('countrycode') || msg.includes('country code')) {
                    targetObj['countryCode'] = messageStr;
                } else if (msg.includes('email')) {
                    targetObj['email'] = messageStr;
                } else if (msg.includes('phone')) {
                    targetObj['phone'] = messageStr;
                } else if (msg.includes('name')) {
                    targetObj['name'] = messageStr;
                } else if (msg.includes('slug')) {
                    targetObj['slug'] = messageStr;
                } else if (msg.includes('location')) {
                    targetObj['location'] = messageStr;
                } else if (msg.includes('website')) {
                    targetObj['website'] = messageStr;
                } else if (msg.includes('tripadvisor')) {
                    targetObj['tripAdvisorUrl'] = messageStr;
                } else if (msg.includes('map') || msg.includes('iframe')) {
                    targetObj['mapIframe'] = messageStr;
                } else if (msg.includes('description') && !msg.includes('metadescription') && !msg.includes('meta description')) {
                    targetObj['description'] = messageStr;
                } else if (msg.includes('image') || msg.includes('logo')) {
                    targetObj['logo'] = messageStr;
                } else if (msg.includes('heading')) {
                    targetObj['heading'] = messageStr;
                } else if (msg.includes('metatitle') || msg.includes('meta title')) {
                    targetObj['metaTitle'] = messageStr;
                } else if (msg.includes('metadescription') || msg.includes('meta description')) {
                    targetObj['metaDescription'] = messageStr;
                } else if (msg.includes('metakeywords') || msg.includes('meta keywords')) {
                    targetObj['metaKeywords'] = messageStr;
                } else if (msg.includes('metaoutlet') || msg.includes('meta outlet')) {
                    targetObj['metaOutlet'] = messageStr;
                } else if (msg.includes('canonicalurl') || msg.includes('canonical url')) {
                    targetObj['canonicalUrl'] = messageStr;
                } else if (msg.includes('isindex') || msg.includes('is index')) {
                    targetObj['isIndex'] = messageStr;
                }
            };

            if (responseData?.details && Array.isArray(responseData.details)) {
                const errorsObj = {};
                responseData.details.forEach(item => {
                    if (item.field && item.message) {
                        const fieldName = item.field.toLowerCase();
                        if (fieldName === 'general') {
                            mapMessageToField(item.message, errorsObj);
                        } else if (fieldName === 'countrycode' || fieldName === 'country_code') {
                            errorsObj['countryCode'] = item.message;
                        } else if (fieldName === 'organizerimages' || fieldName === 'logo' || fieldName === 'file') {
                            errorsObj['logo'] = item.message;
                        } else if (fieldName === 'mapiframe' || fieldName === 'map_iframe') {
                            errorsObj['mapIframe'] = item.message;
                        } else {
                            errorsObj[item.field] = item.message;
                        }
                    }
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.message || "Validation failed");
                }
            } else if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                const errorsObj = {};
                responseData.errors.forEach(errStr => {
                    mapMessageToField(errStr, errorsObj);
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.errors.join(', '));
                }
            } else {
                const errorsObj = {};
                mapMessageToField(errMsg, errorsObj);
                if (Object.keys(errorsObj).length > 0) {
                    setFieldErrors(errorsObj);
                } else {
                    setModalError(errMsg);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteOrganizer(selectedId);
                if (response && response.success) {
                    showSnackbar("Organizer deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchOrganizers();
                } else {
                    const errMsg = response?.message || "Failed to delete organizer";
                    setDeleteError(errMsg);
                    showSnackbar(errMsg, "error");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const responseData = err.response?.data;
                const errorMessage = responseData?.errors?.[0] ||
                    responseData?.details?.[0]?.message ||
                    responseData?.message ||
                    err.message ||
                    "Failed to delete organizer";
                setDeleteError(errorMessage);
                showSnackbar(errorMessage, "error");
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Search Organized
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search organizers"
                            variant="outlined"
                            size="small"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            sx={{
                                width: { xs: '100%', sm: 260 },
                                bgcolor: 'white',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                        <Search size={16} color="#666" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Select
                            size="small"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            displayEmpty
                            sx={{
                                minWidth: 130,
                                bgcolor: 'white',
                                height: '40px',
                                borderRadius: '6px',
                                fontSize: '0.85rem',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' },
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Status</MenuItem>
                            <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Active</MenuItem>
                            <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Inactive</MenuItem>
                        </Select>
                        <PermissionWrapper permission="create" mode="disable">
                            <Button
                                disableElevation disableRipple disableFocusRipple disableTouchRipple
                                variant="contained"
                                startIcon={<Plus size={18} />}
                                onClick={() => handleOpenModal()}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    px: 2.5,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add Organizer
                            </Button>
                        </PermissionWrapper>
                    </Box>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1250 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '80px' }}>Logo</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '200px' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Slug</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Contact</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : organizers.length > 0 ? (
                                    organizers.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
                                                {(pagination.currentPage - 1) * pagination.pageSize + idx + 1}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                {item.logo && (
                                                    <Box
                                                        component="img"
                                                        src={item.logo}
                                                        alt={item.name}
                                                        sx={{ width: 40, height: 32, objectFit: 'contain', borderRadius: '4px', border: '1px solid #EEE', bgcolor: '#F9F9F9', p: 0.25 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}>
                                                    {item.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.slug}>
                                                {item.slug || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack spacing={0.5}>
                                                    {item.email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Mail size={12} color="#666" />
                                                            <Typography sx={{ color: '#555', fontSize: '0.75rem' }}>{item.email}</Typography>
                                                        </Box>
                                                    )}
                                                    {item.phone && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Phone size={12} color="#666" />
                                                            <Typography sx={{ color: '#555', fontSize: '0.75rem' }}>
                                                                {item.countryCode ? `${item.countryCode} ` : ''}{item.phone}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                {item.location && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <MapPin size={12} color="#666" />
                                                        <Typography sx={{ color: '#555', fontSize: '0.75rem' }}>{item.location}</Typography>
                                                    </Box>
                                                )}
                                                {item.mapIframe && (
                                                    <Box sx={{ mt: 0.3 }}>
                                                        <Typography
                                                            component="a"
                                                            href={item.mapIframe.match(/src=["']([^"']+)["']/i) ? item.mapIframe.match(/src=["']([^"']+)["']/i)[1] : item.mapIframe}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                color: '#1D4ED8',
                                                                textDecoration: 'none',
                                                                fontSize: '0.72rem',
                                                                fontWeight: 500,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 0.3,
                                                                '&:hover': { textDecoration: 'underline' }
                                                            }}
                                                        >
                                                            View on Map
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.5,
                                                        py: 0.3,
                                                        borderRadius: '4px',
                                                        bgcolor: item.isActive ? '#E8F5E9' : '#FFEBEE',
                                                        color: item.isActive ? '#2E7D32' : '#D32F2F',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {item.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No organizers found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Pagination */}
                <GlobalPagination
                    page={filters.page}
                    pageSize={filters.pageSize}
                    totalRecords={pagination.totalRecords}
                    totalPages={pagination.totalPages}
                    onPageChange={(newPage) => handleFilterChange('page', newPage)}
                    onPageSizeChange={(newSize) => {
                        setFilters(prev => ({ ...prev, pageSize: newSize, page: 1 }));
                    }}
                    loading={loading}
                />

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Organizer' : 'Add Organizer'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && <Alert severity="error" sx={{ mb: 2, borderRadius: '6px', fontSize: '0.8rem' }}>{modalError}</Alert>}
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Name"
                                required
                                name="name" size='small'
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. Wellness Organizers Inc."
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Slug"
                                name="slug"
                                size='small'
                                value={formData.slug}
                                onChange={handleSlugChange}
                                onBlur={handleSlugBlur}
                                placeholder="e.g. wellness-organizers-inc"
                                required
                                error={Boolean(fieldErrors.slug)}
                                helperText={fieldErrors.slug || "Automatically generated from name."}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        fontFamily: 'monospace',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                required
                                type="email" size='small'
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="organizer@example.com"
                                error={Boolean(fieldErrors.email)}
                                helperText={fieldErrors.email}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                                <Select
                                    size="small"
                                    id="organizer-country-code-select"
                                    name="countryCode"
                                    value={formData.countryCode || defaultCountryCode}
                                    onChange={handleInputChange}
                                    renderValue={(selected) => {
                                        const item = displayCountryCodes.find(c => c.countryCode === selected);
                                        return (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                {item?.symbol && (
                                                    <Avatar
                                                        src={item.symbol}
                                                        variant="rounded"
                                                        sx={{ width: 18, height: 13, borderRadius: '2px' }}
                                                        imgProps={{ style: { objectFit: 'cover' } }}
                                                    />
                                                )}
                                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1 }}>
                                                    {selected}
                                                </Typography>
                                            </Box>
                                        );
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                width: 220,
                                                maxHeight: 280,
                                                borderRadius: '6px',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                            }
                                        }
                                    }}
                                    sx={{
                                        width: '95px',
                                        minWidth: '95px',
                                        flexShrink: 0,
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& .MuiSelect-select': {
                                            py: '8.5px',
                                            pr: '26px !important',
                                            pl: 1,
                                            display: 'flex',
                                            alignItems: 'center'
                                        },
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }}
                                >
                                    {displayCountryCodes.map((item) => (
                                        <MenuItem key={item.id || item.countryCode} value={item.countryCode}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                {item.symbol && (
                                                    <Avatar
                                                        src={item.symbol}
                                                        variant="rounded"
                                                        sx={{ width: 20, height: 14, borderRadius: '2px' }}
                                                        imgProps={{ style: { objectFit: 'cover' } }}
                                                    />
                                                )}
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                                    {item.countryCode}
                                                </Typography>
                                                {item.name && (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#888', ml: 'auto', pl: 1 }}>
                                                        {item.name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>

                                <TextField
                                    fullWidth
                                    label="Phone"
                                    required
                                    name="phone"
                                    size='small'
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="1234567890"
                                    inputProps={{ maxLength: 10 }}
                                    error={Boolean(fieldErrors.phone)}
                                    helperText={fieldErrors.phone}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                label="Location" size='small'
                                name="location"
                                required
                                value={formData.location}
                                onChange={handleInputChange}
                                placeholder="e.g. New York, USA"
                                error={Boolean(fieldErrors.location)}
                                helperText={fieldErrors.location}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Website / Social links   " size='small'
                                name="website"
                                required
                                value={formData.website}
                                onChange={handleInputChange}
                                placeholder="https://example.com"
                                error={Boolean(fieldErrors.website)}
                                helperText={fieldErrors.website}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth size='small'
                                label="TripAdvisor URL"
                                name="tripAdvisorUrl"
                                value={formData.tripAdvisorUrl}
                                onChange={handleInputChange}
                                placeholder="https://tripadvisor.com/..."
                                error={Boolean(fieldErrors.tripAdvisorUrl)}
                                helperText={fieldErrors.tripAdvisorUrl}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                size='small'
                                fullWidth
                                label="Map Iframe (Google Maps Link)"
                                name="mapIframe"
                                required
                                value={formData.mapIframe}
                                onChange={handleInputChange}
                                multiline
                                rows={2}
                                placeholder='<iframe src="https://www.google.com/maps/embed?..." ...></iframe> or https://maps.google.com/...'
                                error={Boolean(fieldErrors.mapIframe)}
                                helperText={fieldErrors.mapIframe}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth multiline rows={3}
                                label="Description"
                                required
                                name="description" size='small'
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Professional retreat organizer with 10+ years experience"
                                error={Boolean(fieldErrors.description)}
                                helperText={fieldErrors.description}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        name="isActive"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                        }}
                                    />
                                }
                                label={<Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>}
                            />

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: fieldErrors.logo ? '#FF4D49' : '#333', fontSize: '0.85rem' }}>
                                    Logo Image
                                </Typography>
                                <Button
                                    disableRipple disableFocusRipple disableTouchRipple
                                    component="label"
                                    variant="outlined"
                                    startIcon={<Upload size={16} />}
                                    sx={{
                                        borderColor: fieldErrors.logo ? '#FF4D49' : '#E8E8E8',
                                        color: '#555',
                                        borderRadius: '6px',
                                        textTransform: 'none',
                                        height: '40px',
                                        fontSize: '0.85rem',
                                        '&:hover': { borderColor: '#FFBD65' }
                                    }}
                                >
                                    Choose Logo
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*"
                                        onChange={handleLogoChange}
                                    />
                                </Button>
                                {formData.logo && (
                                    <Typography variant="caption" sx={{ ml: 2, color: '#666', fontSize: '0.75rem' }}>
                                        {formData.logo.name}
                                    </Typography>
                                )}
                                {fieldErrors.logo && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                        {fieldErrors.logo}
                                    </Typography>
                                )}

                                {(logoPreview || formData.logo) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Box
                                            component="img"
                                            src={logoPreview || (formData.logo && URL.createObjectURL(formData.logo))}
                                            alt="Logo Preview"
                                            sx={{ width: 80, height: 80, objectFit: 'contain', borderRadius: '4px', border: '1px solid #E8E8E8', p: 0.5 }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            {/* SEO Module */}
                            <Box sx={{
                                border: '1px solid #E2E8F0',
                                borderRadius: '6px',
                                p: 2,
                                bgcolor: '#FAFAFA'
                            }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                    SEO
                                </Typography>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                    Configure search engine optimization settings for this organizer.
                                </Typography>
                                <Stack spacing={1.5}>
                                    {/* Heading */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Heading"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleInputChange}
                                        placeholder="Enter heading..."
                                        error={Boolean(fieldErrors.heading)}
                                        helperText={fieldErrors.heading}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Meta Title */}
                                    <Box>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Meta Title"
                                            name="metaTitle"
                                            value={formData.metaTitle}
                                            onChange={handleInputChange}
                                            placeholder="Enter meta title..."
                                            inputProps={{ maxLength: 70 }}
                                            error={Boolean(fieldErrors.metaTitle)}
                                            helperText={fieldErrors.metaTitle}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                            {formData.metaTitle?.length || 0} / 60 characters
                                        </Typography>
                                    </Box>

                                    {/* Meta Description */}
                                    <Box>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Meta Description"
                                            name="metaDescription"
                                            value={formData.metaDescription}
                                            onChange={handleInputChange}
                                            placeholder="Enter meta description..."
                                            inputProps={{ maxLength: 180 }}
                                            error={Boolean(fieldErrors.metaDescription)}
                                            helperText={fieldErrors.metaDescription}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                            {formData.metaDescription?.length || 0} / 160 characters
                                        </Typography>
                                    </Box>

                                    {/* Meta Keywords */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Meta Keywords"
                                        name="metaKeywords"
                                        value={formData.metaKeywords}
                                        onChange={handleInputChange}
                                        placeholder="Enter meta keywords (comma separated)..."
                                        error={Boolean(fieldErrors.metaKeywords)}
                                        helperText={fieldErrors.metaKeywords || "Comma separated keywords"}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Meta Outlet */}
                                    <Box>
                                        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.825rem', color: '#334155' }}>
                                            Meta Outlet (H2 Outlet)
                                        </Typography>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                            Add meta outlet bullet points for this organizer.
                                        </Typography>

                                        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Add meta outlet item..."
                                                value={newMetaOutletInput}
                                                onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddMetaOutletItem();
                                                    }
                                                }}
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        bgcolor: '#FFFFFF',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                startIcon={<Plus size={16} />}
                                                onClick={handleAddMetaOutletItem}
                                                sx={{
                                                    backgroundColor: '#1E293B',
                                                    color: '#FFFFFF',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    borderRadius: '6px',
                                                    fontWeight: 500,
                                                    px: 2.5,
                                                    fontSize: '0.8rem',
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Stack>

                                        {formData.metaOutlet && formData.metaOutlet.length > 0 && (
                                            <Stack spacing={1}>
                                                {formData.metaOutlet.map((item, idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            p: 1.25,
                                                            backgroundColor: '#FFFFFF',
                                                            border: '1px solid #E2E8F0',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                            • {item}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveMetaOutletItem(idx)}
                                                            sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                        >
                                                            <Trash2 size={15} />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                        {fieldErrors.metaOutlet && (
                                            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                {fieldErrors.metaOutlet}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Canonical URL */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Canonical URL"
                                        name="canonicalUrl"
                                        value={formData.canonicalUrl}
                                        onChange={handleInputChange}
                                        placeholder="Enter canonical URL..."
                                        error={Boolean(fieldErrors.canonicalUrl)}
                                        helperText={fieldErrors.canonicalUrl}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Is Index */}
                                    <Box>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.825rem', fontWeight: 500, color: "#334155" }}>
                                            Is Index
                                        </Typography>
                                        <Box
                                            sx={{
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                p: 1.25,
                                                bgcolor: '#FFFFFF'
                                            }}
                                        >
                                            <Stack direction="row" spacing={3} alignItems="center">
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, isIndex: true }));
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => {
                                                                const updated = { ...prev };
                                                                delete updated.isIndex;
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="organizerIsIndex"
                                                        checked={formData.isIndex === true}
                                                        onChange={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: true }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                        style={{ cursor: 'pointer', margin: 0 }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>True (Indexable)</Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, isIndex: false }));
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => {
                                                                const updated = { ...prev };
                                                                delete updated.isIndex;
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="organizerIsIndex"
                                                        checked={formData.isIndex === false}
                                                        onChange={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: false }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                        style={{ cursor: 'pointer', margin: 0 }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>False (No Index)</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        {fieldErrors.isIndex && (
                                            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                {fieldErrors.isIndex}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',

                                color: '#fff',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Organizer"
                    description="Are you sure you want to delete this organizer? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetAllOrganizers;
